/**
 * Canonical pricing insight data sources (stored in system_settings.pricing_data_source).
 * Legacy values are normalized at API boundaries.
 */

export const PRICING_DATA_SOURCES = [
  "market_reference",
  "approved_listings",
  "combined",
] as const;

export type PricingDataSource = (typeof PRICING_DATA_SOURCES)[number];

/** @deprecated Stored values before rename — normalized on read */
export const LEGACY_PRICING_DATA_SOURCES = [
  "google_sheets",
  "supabase",
  "both",
] as const;

export type LegacyPricingDataSource =
  (typeof LEGACY_PRICING_DATA_SOURCES)[number];

export type PricingDataSourceInput =
  | PricingDataSource
  | LegacyPricingDataSource
  | string
  | null
  | undefined;

const LEGACY_TO_CANONICAL: Record<LegacyPricingDataSource, PricingDataSource> =
  {
    google_sheets: "market_reference",
    supabase: "approved_listings",
    both: "combined",
  };

export const DEFAULT_PRICING_DATA_SOURCE: PricingDataSource = "combined";

export function isPricingDataSource(value: string): value is PricingDataSource {
  return (PRICING_DATA_SOURCES as readonly string[]).includes(value);
}

export function isLegacyPricingDataSource(
  value: string,
): value is LegacyPricingDataSource {
  return (LEGACY_PRICING_DATA_SOURCES as readonly string[]).includes(value);
}

/**
 * Map stored or query values to a canonical source. Unknown values fall back to default.
 */
export function normalizePricingDataSource(
  value: PricingDataSourceInput,
  fallback: PricingDataSource = DEFAULT_PRICING_DATA_SOURCE,
): PricingDataSource {
  if (value == null || value === "") {
    return fallback;
  }
  const trimmed = String(value).trim();
  if (isPricingDataSource(trimmed)) {
    return trimmed;
  }
  if (isLegacyPricingDataSource(trimmed)) {
    return LEGACY_TO_CANONICAL[trimmed];
  }
  return fallback;
}

export function parsePricingDataSourceQuery(
  value: string | null,
  fallback: PricingDataSource = DEFAULT_PRICING_DATA_SOURCE,
): PricingDataSource {
  if (!value) {
    return fallback;
  }
  return normalizePricingDataSource(value, fallback);
}

/** Accept canonical + legacy query/body values */
export function isValidPricingDataSourceInput(
  value: string | null | undefined,
): boolean {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  return isPricingDataSource(trimmed) || isLegacyPricingDataSource(trimmed);
}

export const PRICING_DATA_SOURCE_SELECT_OPTIONS: Array<{
  value: PricingDataSource;
  label: string;
  description: string;
}> = [
  {
    value: "approved_listings",
    label: "SIO listings",
    description:
      "Sell It Off listing comps (approved, completed sales, eligible archived)",
  },
  {
    value: "market_reference",
    label: "Market reference",
    description:
      "Synced external market rows (pricing_reference_listings); price insights read the database, not live Sheets",
  },
  {
    value: "combined",
    label: "Combined",
    description: "Merge SIO listing comps and market reference aggregates",
  },
];

export function getPricingDataSourceLabel(
  dataSource: PricingDataSourceInput,
): string {
  const canonical = normalizePricingDataSource(dataSource);
  const option = PRICING_DATA_SOURCE_SELECT_OPTIONS.find(
    (o) => o.value === canonical,
  );
  return option?.label ?? canonical;
}

/** Whether admin modal comps should include SIO listing pool rows. */
export function showsSioListingComps(dataSource: PricingDataSource): boolean {
  return dataSource === "approved_listings" || dataSource === "combined";
}

/** Whether admin modal comps should include synced market reference rows. */
export function showsMarketReferenceComps(
  dataSource: PricingDataSource,
): boolean {
  return dataSource === "market_reference" || dataSource === "combined";
}

export function getPricingDataSourceBadgeLabel(
  dataSource: PricingDataSourceInput,
): string {
  const canonical = normalizePricingDataSource(dataSource);
  if (canonical === "market_reference") {
    return "Market reference (synced)";
  }
  if (canonical === "combined") {
    return "SIO listings + market reference";
  }
  return "SIO listings";
}

/** Admin combined badge detail — which branch drove band stats (Phase 3). */
export function getCombinedCohortProvenanceLabel(provenance: {
  primaryBranch?: "approved_listings" | "market_reference";
  branches: Array<"approved_listings" | "market_reference">;
}): string | null {
  if (provenance.branches.length === 0) {
    return null;
  }
  if (provenance.primaryBranch === "market_reference") {
    return "Band uses market reference cohort (SIO cohort too thin)";
  }
  if (provenance.primaryBranch === "approved_listings") {
    return "Band uses SIO listing cohort (market reference cohort too thin)";
  }
  if (provenance.branches.length === 2) {
    return "Band uses SIO listings + market reference cohorts";
  }
  if (provenance.branches[0] === "market_reference") {
    return "Band uses market reference only";
  }
  return "Band uses SIO listings only";
}
